/**
 * A3: monthly statement download (PDF via authed blob — never window.open).
 */

import React, { useState } from 'react';
import { customerPortalApi, saveBlob } from '../../../services/customerPortalApi';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';

interface Props {
  accountUuid: string;
  accountNumber: string;
  language?: PortalLanguage;
}

export function statementMonthOptions(now: Date): { year: number; month: number; label: string }[] {
  const options: { year: number; month: number; label: string }[] = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    });
  }
  return options;
}

const StatementsCard: React.FC<Props> = ({ accountUuid, accountNumber, language = 'en' }) => {
  const copy = PORTAL_COPY[language];
  const options = statementMonthOptions(new Date());
  const [selected, setSelected] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const download = async () => {
    const { year, month } = options[selected];
    setDownloading(true);
    setError(false);
    try {
      const blob = await customerPortalApi.downloadStatementPdf(accountUuid, year, month);
      saveBlob(blob, `statement-${accountNumber}-${year}-${String(month).padStart(2, '0')}.pdf`);
    } catch {
      setError(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-base font-semibold text-gray-900">{copy.statementsTitle}</h2>
      </div>
      <div className="p-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <label className="flex-1 text-sm text-gray-700">
          {copy.statementsMonthLabel}
          <select
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {options.map((o, idx) => (
              <option key={`${o.year}-${o.month}`} value={idx}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={download}
          disabled={downloading}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {copy.statementsDownload}
        </button>
      </div>
      {error && <p className="px-6 pb-4 text-sm text-red-600">{copy.loadError}</p>}
    </div>
  );
};

export default StatementsCard;
